import { useRef, useState } from "react";
import { useI18n } from "react-simple-i18n";
import { styled } from "@mui/material/styles";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';

import { FirstButton, FirstOutlineButton, TransparentButton } from "../../components/buttons/buttons";
import { useGlobalContext } from "../../provider";
import { parseNumber, toastMessage } from "../../utils";
import { apiNotification } from "../../services/apiNotification";
import { TextField } from "../../components/inputs/textfield";
import { Select } from "../../components/inputs/select";
import { languageLists, lectureTypes } from "../../config/config";
import { AddItem, AddItemRefProps } from "../../components/inputs/addItem";
import { ValidateError } from "../../utils/customError";
import { restApi } from "../../provider/restApi";

interface EditCourseProps {
	data: CourseDetailObject
	setUpdateDetail: (s: boolean) => void
	updateDetail: boolean
}

interface EditFormObject {
	title: string
	description: string
	price: number
	lectureType: string
	language: string
	learnOutcomes: string[]

	errorTitle: boolean
	errorDesciption: boolean
	errorPrice: boolean
	errorLectureType: boolean
	errorLanguage: boolean
	errorLecture: boolean
	enableSave: boolean
	priceHelperText: string
}

const initEditForm: EditFormObject = {
	title: "",
	description: "",
	price: 0,
	lectureType: "",
	language: "",
	learnOutcomes: [],

	errorTitle: false,
	errorDesciption: false,
	errorPrice: false,
	errorLectureType: false,
	errorLanguage: false,
	errorLecture: false,
	enableSave: false,
	priceHelperText: "",
}

const EditCourse = ({ data, setUpdateDetail, updateDetail }: EditCourseProps) => {
	const { t } = useI18n();
	const itemsRef = useRef<AddItemRefProps>(); // Reference to the child component
	const [isEditing, setIsEditing] = useState(false);
	const [editForm, setEditForm] = useState<EditFormObject>(initEditForm);
	const [globalState, { dispatch }]: GlobalContextType = useGlobalContext();

	const updateEditFormData = (data: Partial<EditFormObject>) => {
		setEditForm(prev => ({ ...prev, ...data, enableSave: true }))
	}

	const checkValidate = (data: EditFormObject): boolean => {
		if (!data.title) return false;
		if (!data.description) return false;
		if (!data.price) return false;
		if (!data.lectureType) return false;
		if (!data.language) return false;
		if (data.price < globalState.minimumAmount) return false;

		return true;
	}

	const handleSave = async () => {
		try {
			if (!itemsRef.current) {
				throw new ValidateError("Try after page reload!");
			}

			const validate = checkValidate(editForm);
			const learnOutcomesValidate = itemsRef.current.validateItem();

			if (!validate || !learnOutcomesValidate) {
				let errorPriceMessage = "";
				if (editForm.price < globalState.minimumAmount) {
					errorPriceMessage = `Minimum price is ${globalState.minimumAmount} USD`;
				}

				updateEditFormData({
					errorTitle: !editForm.title,
					errorDesciption: !editForm.description,
					errorLectureType: !editForm.lectureType,
					errorLanguage: !editForm.language,
					errorPrice: !editForm.price || !!errorPriceMessage,
					priceHelperText: errorPriceMessage
				})

				return false;
			}

			const updateData: UpdateCourseProps = {
				courseId: data.id,
				title: editForm.title,
				description: editForm.description,
				price: editForm.price,
				lectureType: editForm.lectureType,
				language: editForm.language,
				learnOutcomes: editForm.learnOutcomes,
			}

			dispatch({ loading: true });
			await restApi.updateCourse(updateData);

			setIsEditing(false);
			setUpdateDetail(!updateDetail);
			dispatch({ loading: false });
			toastMessage("success", "Course updated!");
		} catch (err: any) {
			dispatch({ loading: false });
			apiNotification(err, "Course update failed!");
		}
	}

	const onOpenEditModal = () => {
		setEditForm({
			...initEditForm,

			title: data.title,
			description: data.description,
			price: data.price,
			lectureType: data.lectureType,
			language: data.language,
			learnOutcomes: data.learnOutcomes,
			enableSave: false,
		})

		setIsEditing(true);
	}

	const onCloseEditModal = () => {
		setIsEditing(false);
	}

	const onChangeInputValue = (key: string) => (e: any) => {
		const { name, value } = e.target;

		if (key === "errorPrice") {
			updateEditFormData({ [name]: parseNumber(value), [key]: false });
		} else {
			updateEditFormData({ [name]: value, [key]: false });
		}
	}

	return (
		<EditCourseWrapper>
			<EditButton fullWidth onClick={onOpenEditModal}>
				<EditIcon fontSize="small" />
				<Typography variant="body1">{t("buttons.edit")}</Typography>
			</EditButton>

			{/* Edit Profile Modal */}
			<Dialog fullWidth
				open={isEditing}
				onClose={onCloseEditModal}
				maxWidth="sm"
			>
				<DialogTitle>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<EditIcon color="primary" fontSize="small" />
						<Typography variant="h6">Edit Course</Typography>
					</Box>
				</DialogTitle>

				<DialogContent dividers>
					<EditForm>
						<TextField fullWidth
							value={editForm.title}
							label={t("inputLabels.title")} name="title"
							onChange={onChangeInputValue("errorTitle")}
							error={editForm.errorTitle}
						/>

						<TextField fullWidth
							value={editForm.description}
							label={t("inputLabels.description")} name="description"
							onChange={onChangeInputValue("errorDesciption")}
							error={editForm.errorDesciption}
							multiline rows={4}
						/>

						<TextField fullWidth
							value={editForm.price}
							label={t("inputLabels.price")} name="price" type="number"
							onChange={onChangeInputValue("errorPrice")}
							InputProps={{ startAdornment: "$" }}
							error={editForm.errorPrice}
							helperText={editForm.priceHelperText}
							className="base-filled"
						/>

						<Select value={editForm.lectureType}
							label={t("inputLabels.type")} name="lectureType"
							onChange={onChangeInputValue("errorLectureType")}
							error={editForm.errorLectureType}
							items={lectureTypes}
						/>

						<Select value={editForm.language}
							onChange={onChangeInputValue("errorLanguage")}
							label={t("inputLabels.language")} name="language"
							error={editForm.errorLanguage}
							items={languageLists}
						/>

						{/* learn Outcomes */}
						<AddItem ref={itemsRef}
							label={t("inputLabels.whatYouWillLearn")}
							onChange={(value: string[]) => { updateEditFormData({ learnOutcomes: value }) }}
							value={editForm.learnOutcomes}
						/>
					</EditForm>
				</DialogContent>

				<DialogActions sx={{ padding: 2 }}>
					<FirstOutlineButton onClick={onCloseEditModal}>
						<CloseIcon fontSize="small" />
						<Typography variant="body1">{t("buttons.cancel")}</Typography>
					</FirstOutlineButton>

					<FirstButton disabled={!editForm.enableSave} onClick={handleSave}>
						<SaveIcon fontSize="small" />
						<Typography variant="body1">{t("buttons.saveChange")}</Typography>
					</FirstButton>
				</DialogActions>
			</Dialog>
		</EditCourseWrapper>
	)
}

const EditCourseWrapper = styled(Box)(({ theme }) => ({

}))

const EditButton = styled(TransparentButton)(({ theme }) => ({
	marginTop: theme.spacing(2),

	p: {
		fontWeight: 400,
		color: `${theme.palette.common.thirdColor} !important`,
	},
}))

const EditForm = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(3),
	padding: theme.spacing(1),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
		padding: theme.spacing(0),
	}
}))

export { EditCourse }